import React, { useState, useEffect, useCallback } from 'react';
import './Collections.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const emptyItem = { title: '', subtitle: '', image_url: '', link_url: '', button_text: 'Shop Now', sort_order: 0, is_active: true };

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/collections`);
      const data = await res.json();
      setCollections(data);
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  const getImageUrl = (imgPath) => {
    if (!imgPath) return '';
    if (imgPath.startsWith('http')) return imgPath;
    if (imgPath.startsWith('/uploads/')) {
      const baseUrl = API_URL.replace('/api', '');
      return `${baseUrl}${imgPath}`;
    }
    return imgPath;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/collections/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setEditing(prev => ({ ...prev, image_url: data.imageUrl }));
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const method = editing.id ? 'PUT' : 'POST';
    const url = editing.id ? `${API_URL}/collections/${editing.id}` : `${API_URL}/collections`;
    try {
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editing),
      });
      setEditing(null);
      fetchCollections();
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/collections/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCollections();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleToggle = async (item) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/collections/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...item, is_active: !item.is_active }),
      });
      fetchCollections();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  if (loading) return <div className="collections-loading">Loading...</div>;

  // Form view
  if (editing) {
    return (
      <div className="collections-page">
        <div className="collections-header">
          <h2>{editing.id ? 'Edit Collection' : 'Add Collection'}</h2>
          <button className="btn-back" onClick={() => setEditing(null)}>← Back</button>
        </div>
        <div className="collection-form">
          <label>Title
            <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. OPI Professional" />
          </label>
          <label>Subtitle
            <input value={editing.subtitle} onChange={e => setEditing({ ...editing, subtitle: e.target.value })} placeholder="e.g. Iconic shades, unbeatable formula" />
          </label>
          <label>Image
            <div className="collection-image-upload">
              {editing.image_url ? (
                <div className="collection-image-preview">
                  <img src={getImageUrl(editing.image_url)} alt="Preview" onError={e => e.target.style.display = 'none'} />
                  <button className="btn-remove-image" onClick={() => setEditing({ ...editing, image_url: '' })}>✕</button>
                </div>
              ) : (
                <label className="btn-upload-collection-img">
                  {uploading ? 'Uploading...' : 'Choose Image'}
                  <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={uploading} />
                </label>
              )}
            </div>
          </label>
          <label>Link URL
            <input value={editing.link_url} onChange={e => setEditing({ ...editing, link_url: e.target.value })} placeholder="/brands/opi" />
          </label>
          <label>Button Text
            <input value={editing.button_text} onChange={e => setEditing({ ...editing, button_text: e.target.value })} placeholder="Shop Now" />
          </label>
          <label>Sort Order
            <input type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
            Active
          </label>

          <div className="form-actions">
            <button className="btn-save" onClick={handleSave}>Save</button>
            <button className="btn-cancel" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="collections-page">
      <div className="collections-header">
        <h2>Homepage Collections</h2>
        <button className="btn-add" onClick={() => setEditing({ ...emptyItem })}>+ Add Collection</button>
      </div>
      <p className="collections-desc">Manage the featured collections shown on the homepage.</p>

      <div className="collections-table">
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Title</th>
              <th>Subtitle</th>
              <th>Link</th>
              <th>Order</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {collections.map(item => (
              <tr key={item.id} className={!item.is_active ? 'inactive-row' : ''}>
                <td><img src={getImageUrl(item.image_url)} alt={item.title} className="table-thumb" onError={e => e.target.style.display = 'none'} /></td>
                <td>{item.title}</td>
                <td>{item.subtitle}</td>
                <td><code>{item.link_url}</code></td>
                <td>{item.sort_order}</td>
                <td>
                  <button className={`status-badge ${item.is_active ? 'active' : 'inactive'}`} onClick={() => handleToggle(item)}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="actions-cell">
                  <button className="btn-edit" onClick={() => setEditing({ ...item })}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {collections.length === 0 && (
              <tr><td colSpan="7" className="empty-row">No collections yet. Add one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Collections;
